import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Button, Stack } from '@mui/material'
import { observer } from 'mobx-react'
import React from 'react'
import { useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { useInvalidateQueries } from '../../../../../hooks/useInvalidateQueries'
import { isAdminLabelAA } from '../../../../../services/AccountService'
import { updateRequest } from '../../../../../services/ApiService'
import { notifySuccess } from '../../../../../services/ToastifyService/ToastifyService'
import { getComp } from '../../../../../utils/utils/DI'
import { dataEditReportStatuses } from '../../../consts/request-body-data'
import singleRequestStore from '../../../store/SingleRequestStore'
import { RequestType } from '../../../types/Request.type'
import FormInput from '../elements/FormInput'
import FormSelect from '../elements/FormSelect'
import InputLabel from '../elements/InputLabel'
import TypedAccordion from '../elements/TypedAccordion'
import { getDefaultDetailsValues } from '../helpers/getDefaultValues'
import {
  BaseOptionType,
  ChangeDetailsFormType,
  getChangeDetailsSchema,
} from '../helpers/getFormSchema'

type DrawerContentRequestDetailsProps = {
  request: RequestType
  onClose: () => void
}

type ParamsType = {
  reasonId?: number
  senderId?: number
  updateComment?: string
}

export const DrawerContentRequestDetails: React.FC<
  DrawerContentRequestDetailsProps
> = ({ request, onClose }) => {
  const invalidate = useInvalidateQueries()
  const { reasons } = getComp('InstallationReasonStore')
  const { shownSenders } = getComp('SendersStore')
  const intl = useIntl()
  const { updateRequest: handleUpdateRequest, getComments } = singleRequestStore

  const { control, handleSubmit, watch } = useForm<ChangeDetailsFormType>({
    defaultValues: getDefaultDetailsValues(request),
    mode: 'onChange',
    resolver: zodResolver(
      getChangeDetailsSchema(
        getFeatureState(request, null, null, null).inReportEditStatus,
      ),
    ),
  })

  const { disableSaveBtn, inReportEditStatus, isSenderChange } =
    getFeatureState(
      request,
      watch('senderId'),
      watch('reasonId'),
      watch('updateComment'),
    )

  const onSave = async (data: ChangeDetailsFormType) => {
    const { reasonId, senderId, updateComment } = data
    const params: ParamsType = {}

    if (reasonId?.key && !inReportEditStatus) params.reasonId = reasonId?.key
    if (senderId?.key) params.senderId = senderId?.key
    if (updateComment?.length) params.updateComment = updateComment

    await updateRequest(request.id, params).then((res: RequestType) => {
      handleUpdateRequest(res)
      invalidate(['requests-table'])
      getComments(request.id)
      notifySuccess('Заявка обновлена')
      onClose()
    })
  }

  const reasonsItems: BaseOptionType[] = reasons.map((reason: any) => ({
    key: reason.value,
    value: reason.key,
  }))

  const sendersItems: BaseOptionType[] = shownSenders.map((sender: any) => ({
    key: sender.id,
    value: sender.name,
  }))

  return (
    <Stack
      height={'100%'}
      justifyContent={'space-between'}
      component={'form'}
      onSubmit={handleSubmit(onSave)}
    >
      <TypedAccordion summary={'Данные заявки'}>
        <Stack spacing='8px' py={'16px'}>
          <InputLabel label='Причина обращения'>
            <FormSelect
              control={control}
              fullWidth
              name='reasonId'
              items={reasonsItems}
              placeholder='Выберите причину обращения'
              disabled={inReportEditStatus}
            />
          </InputLabel>
          <InputLabel label='Источник'>
            <FormSelect
              control={control}
              fullWidth
              name='senderId'
              items={sendersItems}
              placeholder='Выберите источник'
            />
          </InputLabel>
          {inReportEditStatus && isSenderChange && (
            <Stack spacing={'10px'} padding={'10px 0 0 0'}>
              <FormInput
                control={control}
                name='updateComment'
                multiline
                rows={3}
                fullWidth
                defaultError
                placeholder={intl.formatMessage({ id: 'Введите комментарий' })}
              />
            </Stack>
          )}
        </Stack>
      </TypedAccordion>
      <Box px={2}>
        <Button
          color={'primary'}
          variant={'contained'}
          sx={{ margin: '24px 0 0 0' }}
          fullWidth
          disabled={disableSaveBtn}
          type='submit'
        >
          <FormattedMessage id={'сохранить'} />
        </Button>
        <Button
          color={'primary'}
          variant={'outlined'}
          onClick={onClose}
          fullWidth
          disabled={disableSaveBtn}
          sx={{ margin: '8px 0 24px 0' }}
        >
          <FormattedMessage id={'Отмена'} />
        </Button>
      </Box>
    </Stack>
  )
}

const getFeatureState = (
  request: RequestType,
  senderValue: BaseOptionType | null,
  reasonValue: BaseOptionType | null,
  updateComment: string | null,
) => {
  const state = {
    inReportEditStatus:
      isAdminLabelAA() &&
      request.status === 15 &&
      dataEditReportStatuses.includes(request.reportStatus ?? -1),
    isSenderChange: senderValue?.value !== request.sender,
    disableSaveBtnByReportStatus: false,
    disableSaveBtn: true,
  }

  state.disableSaveBtnByReportStatus =
    state.inReportEditStatus && !updateComment
  state.disableSaveBtn =
    (reasonValue?.key === request.reasonId && !state.isSenderChange) ||
    state.disableSaveBtnByReportStatus

  return state
}

export default observer(DrawerContentRequestDetails)
